export const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const

export const FY_Q_MONTHS: Record<string, number[]> = {
  Q1: [4, 5, 6],
  Q2: [7, 8, 9],
  Q3: [10, 11, 12],
  Q4: [1, 2, 3],
}

export function getMonthsForFY(fyKey: string | null | undefined) {
  if (!fyKey || fyKey === 'all') return null
  const startYear = parseInt(fyKey, 10)
  if (Number.isNaN(startYear)) return null
  const out: { year: number; month: number }[] = []
  for (let m = 4; m <= 12; m++) out.push({ year: startYear, month: m })
  for (let m = 1; m <= 3; m++) out.push({ year: startYear + 1, month: m })
  return out
}

export function getMonthsForFYQ(fyKey: string, q: string) {
  const months = getMonthsForFY(fyKey)
  if (!months || !q || q === 'all') return months
  const qMonths = FY_Q_MONTHS[q]
  if (!qMonths) return months
  return months.filter(({ month }) => qMonths.includes(month))
}

export interface PeriodFilter {
  year: string | number
  month: string | number
  fy: string
  q: string
}

export function isRecordInPeriod(
  year: number,
  month: number,
  filter: PeriodFilter,
): boolean {
  const fY = filter.year
  const fM = filter.month
  const fFY = filter.fy
  const fQ = filter.q
  const y = Number(year)
  const m = Number(month)
  if (fFY && fFY !== 'all') {
    const forQ = getMonthsForFYQ(String(fFY), String(fQ))
    if (forQ?.length)
      return forQ.some((ry) => ry.year === y && ry.month === m)
  }
  if (fY && fY !== 'all') {
    if (fM && fM !== 'all')
      return y === Number(fY) && m === Number(fM)
    return y === Number(fY)
  }
  return true
}

export function getCurrentFYStartYear(): number {
  const d = new Date()
  const year = d.getFullYear()
  const month = d.getMonth() + 1
  return month >= 4 ? year : year - 1
}

/** Keys like `2024-04` for matching `user_summary_stats.by_year_month`. */
export function getYearMonthKeysForFilter(
  filter: PeriodFilter | null | undefined,
): string[] | null {
  if (!filter || (filter.year === 'all' && filter.fy === 'all')) return null
  if (filter.fy && filter.fy !== 'all') {
    const months = getMonthsForFYQ(String(filter.fy), String(filter.q))
    return months
      ? months.map(
          ({ year, month }) =>
            `${year}-${String(month).padStart(2, '0')}`,
        )
      : null
  }
  if (filter.year && filter.year !== 'all') {
    const y = Number(filter.year)
    if (filter.month && filter.month !== 'all')
      return [`${y}-${String(filter.month).padStart(2, '0')}`]
    return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(
      (m) => `${y}-${String(m).padStart(2, '0')}`,
    )
  }
  return null
}

export function buildFYOptions(
  years: number[],
  maxStartYear?: number,
): { value: string; label: string }[] {
  if (!years?.length) return []
  const min = Math.min(...years)
  const max = Math.max(...years)
  const cap =
    maxStartYear != null ? Math.min(max, maxStartYear) : max
  const out: { value: string; label: string }[] = []
  for (let y = min; y <= cap; y++) {
    out.push({
      value: `${y}-${String(y + 1).slice(-2)}`,
      label: `FY ${y}-${String(y + 1).slice(-2)}`,
    })
  }
  return out.reverse()
}
